import { LanguagesEnum } from '@/api/enums';
import { PhishingLanguagesEnum } from '@/api/enums/phishing-languages.enum';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { PhishingTranslationSSEEvent } from '@/client/campaigns';
import { useNotifications } from '@/hooks/notifications.hook';
import {
  MASTER_TEMPLATES_QUERY_KEY,
  usePhishingTranslationStatus,
  useTranslatePhishingTemplate,
} from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import { mapLanguageToPhishingLanguage } from '@/utils/helpers';
import { AxiosError, HttpStatusCode } from 'axios';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { PhishingTranslationProgress } from './PhishingTranslationProgress';
import { PhishingTranslationSettings } from './PhishingTranslationSettings';
import { PhishingTranslationTrigger } from './PhishingTranslationTrigger';
import {
  resolvePhishingTranslationCompletion,
  shouldFinalizeFromStatus,
} from './phishing-translation-completion';

const PHISHING_TRANSLATION_EVENTS: string[] = [
  'phishing-translation.progress',
  'phishing-translation.completed',
  'phishing-translation.failed',
];

const STATUS_POLL_INTERVAL_MS = 3000;
const STATUS_SETTLE_MS = 2500;

type TranslationState = 'idle' | 'settings' | 'translating';

type TranslationProgressState = {
  completedLanguages: LanguagesEnum[];
  failedLanguages: LanguagesEnum[];
  percent: number;
};

type StoredTranslationJob = {
  translationKey: string;
  targetLanguages: LanguagesEnum[];
};

const EMPTY_PROGRESS: TranslationProgressState = {
  completedLanguages: [],
  failedLanguages: [],
  percent: 0,
};

const sameLanguageList = (a: LanguagesEnum[], b: LanguagesEnum[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const sameProgress = (
  a: TranslationProgressState,
  b: TranslationProgressState,
): boolean =>
  a.percent === b.percent &&
  sameLanguageList(a.completedLanguages, b.completedLanguages) &&
  sameLanguageList(a.failedLanguages, b.failedLanguages);

const getStorageKey = (masterTemplateId: string) =>
  `phishing-translation-${masterTemplateId}`;

const readStoredTranslationJob = (
  masterTemplateId: string,
): StoredTranslationJob | null => {
  try {
    const stored = sessionStorage.getItem(getStorageKey(masterTemplateId));

    if (!stored) return null;

    const parsed = JSON.parse(stored) as Partial<StoredTranslationJob>;

    return parsed?.translationKey
      ? {
          translationKey: parsed.translationKey,
          targetLanguages: parsed.targetLanguages ?? [],
        }
      : null;
  } catch {
    return null;
  }
};

const storeTranslationJob = (
  masterTemplateId: string,
  job: StoredTranslationJob,
) =>
  sessionStorage.setItem(getStorageKey(masterTemplateId), JSON.stringify(job));

const clearStoredTranslationJob = (masterTemplateId: string) =>
  sessionStorage.removeItem(getStorageKey(masterTemplateId));

const getStartErrorMessageKey = (error: AxiosError): string | null => {
  if (error.response?.status === HttpStatusCode.Conflict) {
    return 'phishing.translation.error.inProgress';
  }

  if (error.response?.status !== HttpStatusCode.BadRequest) {
    return null;
  }

  const code = (error.response?.data as { code?: string })?.code;

  if (code === 'ALL_LANGUAGES_LINKED') {
    return 'phishing.translation.error.allTranslated';
  }

  if (code === 'PRIMARY_TEMPLATE_INCOMPLETE') {
    return 'phishing.translation.error.noContent';
  }

  return null;
};

export type PhishingTranslationSectionTemplate = {
  id: string;
  language?: string;
};

export type PhishingTranslationSectionProps = {
  masterTemplateId: string;
  primaryTemplateId: string | null;
  templates: PhishingTranslationSectionTemplate[];
};

export const PhishingTranslationSection: React.FC<
  PhishingTranslationSectionProps
> = ({ masterTemplateId, primaryTemplateId, templates }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [translationState, setTranslationState] =
    useState<TranslationState>('idle');
  const [translationKey, setTranslationKey] = useState<string | null>(null);
  const [languages, setLanguages] = useState<LanguagesEnum[]>([]);
  const [translationProgress, setTranslationProgress] =
    useState<TranslationProgressState>(EMPTY_PROGRESS);
  const [retryLanguages, setRetryLanguages] = useState<LanguagesEnum[]>([]);
  const [pendingRecovery, setPendingRecovery] =
    useState<StoredTranslationJob | null>(null);

  const { translate, isLoading: isStarting } = useTranslatePhishingTemplate();

  const { lastMessage, setNotificationParam } = useNotifications(
    client.masterTemplates.phishingTranslationNotifyUrl,
  );

  const translationStartedAt = useRef<number>(0);

  const isTranslating = translationState === 'translating';

  const {
    translationStatus,
    dataUpdatedAt: statusUpdatedAt,
    isError: isStatusError,
  } = usePhishingTranslationStatus(masterTemplateId, {
    enabled: !!pendingRecovery || isTranslating,
    refetchInterval: isTranslating ? STATUS_POLL_INTERVAL_MS : false,
  });

  const linkedLanguages = useMemo(
    () =>
      templates
        .map(({ language }) =>
          language
            ? mapLanguageToPhishingLanguage(language as LanguagesEnum)
            : null,
        )
        .filter(
          (language): language is PhishingLanguagesEnum => language !== null,
        ),
    [templates],
  );

  const sourceLanguage = useMemo(() => {
    const primaryTemplate = templates.find(
      ({ id }) => id === primaryTemplateId,
    );

    return primaryTemplate?.language
      ? mapLanguageToPhishingLanguage(primaryTemplate.language as LanguagesEnum)
      : null;
  }, [templates, primaryTemplateId]);

  useEffect(() => {
    setPendingRecovery(readStoredTranslationJob(masterTemplateId));
  }, [masterTemplateId]);

  useEffect(() => {
    if (!pendingRecovery || !translationStatus) return;

    setPendingRecovery(null);

    if (!translationStatus.active) {
      clearStoredTranslationJob(masterTemplateId);
      return;
    }

    const activeKey =
      translationStatus.translationKey ?? pendingRecovery.translationKey;
    const completedLanguages = translationStatus.completedLanguages ?? [];
    const failedLanguages = translationStatus.failedLanguages ?? [];

    setTranslationKey(activeKey);
    setLanguages(
      activeKey === pendingRecovery.translationKey
        ? pendingRecovery.targetLanguages
        : [...new Set([...completedLanguages, ...failedLanguages])],
    );
    setTranslationProgress({
      completedLanguages,
      failedLanguages,
      percent: translationStatus.progress ?? 0,
    });
    setTranslationState('translating');
    setNotificationParam(masterTemplateId);
  }, [
    pendingRecovery,
    translationStatus,
    masterTemplateId,
    setNotificationParam,
  ]);

  const finishTranslation = useCallback(() => {
    setNotificationParam(undefined);
    clearStoredTranslationJob(masterTemplateId);
    queryClient.invalidateQueries([MASTER_TEMPLATES_QUERY_KEY]);
    setTranslationKey(null);
  }, [masterTemplateId, queryClient, setNotificationParam]);

  useEffect(() => {
    const event = lastMessage?.data as PhishingTranslationSSEEvent | undefined;

    if (!event || !PHISHING_TRANSLATION_EVENTS.includes(event.event)) return;
    if (!translationKey || event.translationKey !== translationKey) return;

    if (event.event === 'phishing-translation.progress') {
      setTranslationProgress((current) => {
        const next: TranslationProgressState = {
          completedLanguages: event.completedLanguages ?? [],
          failedLanguages: event.failedLanguages ?? [],
          percent: event.progress ?? current.percent,
        };

        return sameProgress(current, next) ? current : next;
      });
    }

    if (event.event === 'phishing-translation.failed') {
      finishTranslation();
      toast?.error(t('error'), t('translations.error.generic'));
      setRetryLanguages(languages);
      setTranslationState('settings');

      return;
    }

    const completion = resolvePhishingTranslationCompletion(event);

    if (!completion) return;

    finishTranslation();
    setRetryLanguages(completion.failedLanguages);
    setTranslationState(
      completion.failedLanguages.length ? 'settings' : 'idle',
    );
  }, [lastMessage, translationKey, languages, finishTranslation, toast, t]);

  useEffect(() => {
    if (isTranslating) {
      translationStartedAt.current = Date.now();
    }
  }, [isTranslating]);

  // Backstop: if the job is no longer running but the spinner is still up, the
  // SSE events were missed entirely (the job finished before this page
  // subscribed). Finalise from the polled status instead of waiting forever.
  useEffect(() => {
    if (!isTranslating) return;

    if (isStatusError || statusUpdatedAt < translationStartedAt.current) {
      return;
    }

    const elapsed = Date.now() - translationStartedAt.current;

    if (
      !shouldFinalizeFromStatus(translationStatus, elapsed, STATUS_SETTLE_MS)
    ) {
      return;
    }

    finishTranslation();

    const failedLanguages = translationProgress.failedLanguages;

    setRetryLanguages(failedLanguages);
    setTranslationState(failedLanguages.length ? 'settings' : 'idle');
  }, [
    isTranslating,
    translationStatus,
    statusUpdatedAt,
    isStatusError,
    translationProgress,
    finishTranslation,
  ]);

  const handleStart = async (targetLanguages: LanguagesEnum[]) => {
    try {
      const { translationKey: startedKey } = await translate({
        masterTemplateId,
        request: { targetLanguages },
      });

      storeTranslationJob(masterTemplateId, {
        translationKey: startedKey,
        targetLanguages,
      });

      setLanguages(targetLanguages);
      setTranslationProgress(EMPTY_PROGRESS);
      setTranslationKey(startedKey);
      setTranslationState('translating');
      setNotificationParam(masterTemplateId);
    } catch (e) {
      const error = e as AxiosError;
      const messageKey = getStartErrorMessageKey(error);

      if (messageKey) {
        toast?.error(t('error'), t(messageKey));

        return;
      }

      handleAxiosError(error, toast);
    }
  };

  const handleCancel = () => {
    setRetryLanguages([]);
    setTranslationState('idle');
  };

  if (translationState === 'translating') {
    return (
      <PhishingTranslationProgress
        languages={languages}
        completedLanguages={translationProgress.completedLanguages}
        failedLanguages={translationProgress.failedLanguages}
        progress={translationProgress.percent}
      />
    );
  }

  if (translationState === 'settings' && sourceLanguage) {
    return (
      <PhishingTranslationSettings
        key={retryLanguages.join('-') || 'initial'}
        sourceLanguage={sourceLanguage}
        linkedLanguages={linkedLanguages}
        initialTargetLanguages={retryLanguages}
        isStarting={isStarting}
        onStart={handleStart}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <PhishingTranslationTrigger
      disabled={!sourceLanguage}
      disabledReasonKey={
        primaryTemplateId
          ? 'phishing.translation.trigger.unsupportedSource'
          : 'phishing.translation.trigger.disabled'
      }
      onClick={() => setTranslationState('settings')}
    />
  );
};
